<template>
  <v-card class="sale-whatsapp-panel" :class="{ dark: isDark }" flat>
    <div class="sale-whatsapp-topbar">
      <div class="client-identity">
        <v-avatar size="42" color="#dff7ea">
          <img v-if="contactPhoto" :src="contactPhoto" />
          <v-icon v-else color="#128C7E">mdi-account</v-icon>
        </v-avatar>
        <div>
          <strong>{{ clientName || "Cliente da venda" }}</strong>
          <small>{{ clientPhone || "Sem telefone cadastrado" }}</small>
        </div>
      </div>
      <div class="topbar-actions">
        <v-menu offset-y left :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn small rounded outlined v-bind="attrs" v-on="on">
              <v-icon small class="mr-1">mdi-account-multiple-plus</v-icon>
              {{ selectedFollowerIds.length }}
            </v-btn>
          </template>
          <v-card class="followers-menu" :class="{ dark: isDark }">
            <div class="followers-title">Envolvidos na venda</div>
            <v-autocomplete
              v-model="selectedFollowerIds"
              :items="employees"
              item-text="nome"
              item-value="_id"
              multiple
              chips
              deletable-chips
              dense
              outlined
              hide-details
              :loading="loadingEmployees"
              placeholder="Adicionar funcionarios"
            />
            <v-btn class="mt-3" block small color="#25D366" dark :loading="savingFollowers" @click="saveFollowers">
              Salvar envolvidos
            </v-btn>
          </v-card>
        </v-menu>
        <v-btn small rounded color="#25D366" dark :disabled="!whatsappConversation" @click="openWhatsappConversation">
          Atendimento
        </v-btn>
      </div>
    </div>

    <div class="sale-whatsapp-status">
      <v-icon small :color="whatsappConversation ? '#25D366' : '#9aa7a0'">
        {{ whatsappConversation ? "mdi-check-circle" : "mdi-alert-circle-outline" }}
      </v-icon>
      <span>{{ conversationStatusText }}</span>
      <v-spacer />
      <v-btn x-small text :loading="loadingConversation" @click="loadWhatsappConversation">Atualizar</v-btn>
    </div>

    <div ref="messageStream" class="sale-whatsapp-stream">
      <div v-if="loadingMessages" class="stream-empty">
        <v-progress-circular indeterminate size="24" color="#25D366" />
        <span>Carregando conversa...</span>
      </div>
      <div v-else-if="!whatsappConversation" class="stream-empty">
        <v-icon size="34" color="#9aa7a0">mdi-whatsapp</v-icon>
        <span>Vincule o telefone do cliente a uma conversa para usar o WhatsApp aqui.</span>
      </div>
      <div v-else-if="!messages.length" class="stream-empty">
        <v-icon size="34" color="#9aa7a0">mdi-message-outline</v-icon>
        <span>Nenhuma mensagem carregada ainda.</span>
      </div>
      <div
        v-for="message in messages"
        v-else
        :key="message._id"
        class="message-row"
        :class="{ outgoing: message.from_me }"
      >
        <div class="message-bubble">
          <img
            v-if="message.message_type === 'image' && message.attachment && message.attachment.previewDataUrl"
            class="message-media"
            :src="message.attachment.previewDataUrl"
            alt="Imagem"
          />
          <audio
            v-else-if="message.message_type === 'audio' && message.attachment && message.attachment.previewDataUrl"
            class="message-audio"
            :src="message.attachment.previewDataUrl"
            controls
          />
          <div v-else-if="message.message_type === 'document'">
            <v-icon small>mdi-file-document-outline</v-icon>
            {{ message.attachment && message.attachment.name }}
          </div>
          <div v-else>{{ message.text || messagePreview(message) }}</div>
          <small>
            {{ formatDate(message.timestamp || message.created_at) }}
            <v-icon v-if="message.from_me" x-small :color="message.delivery_status === 'read' ? '#53bdeb' : '#8a9a93'">
              {{ statusIcon(message.delivery_status) }}
            </v-icon>
          </small>
        </div>
      </div>
    </div>

    <div class="sale-whatsapp-composer">
      <v-text-field
        v-model="newMessage"
        :disabled="!whatsappConversation || sending"
        placeholder="Responder pelo WhatsApp..."
        solo
        dense
        flat
        hide-details
        @keyup.enter="sendMessage"
      />
      <v-btn icon color="#25D366" :loading="sending" :disabled="!canSend" @click="sendMessage">
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { sendWhatsappMessage } from "@/apps/WhatsappAtendimento/runtime";

export default {
  name: "SaleCollaborationPanel",
  data() {
    return {
      selectedFollowerIds: [],
      whatsappConversation: null,
      messages: [],
      newMessage: "",
      loadingConversation: false,
      loadingMessages: false,
      loadingEmployees: false,
      savingFollowers: false,
      sending: false
    };
  },
  computed: {
    ...mapGetters(["get_gestordepedido", "get_funcionarios"]),
    isDark() {
      return localStorage.dark === "true";
    },
    sale() {
      return this.get_gestordepedido || {};
    },
    client() {
      return (this.sale.compra && this.sale.compra.cliente) || {};
    },
    clientName() {
      return this.client.nome || "";
    },
    clientPhone() {
      return this.client.telefone || this.client.phone || "";
    },
    employees() {
      return (this.get_funcionarios && this.get_funcionarios.docs) || [];
    },
    contactPhoto() {
      return this.whatsappConversation && this.whatsappConversation.sender_picture_url;
    },
    conversationStatusText() {
      if (!this.clientPhone) return "Cliente sem telefone cadastrado.";
      if (!this.whatsappConversation) return "Nenhuma conversa encontrada para este cliente.";
      return "Conversa conectada a esta venda.";
    },
    canSend() {
      return Boolean(this.whatsappConversation && this.newMessage.trim() && !this.sending);
    }
  },
  watch: {
    "sale._id": {
      immediate: true,
      handler() {
        this.syncFromSale();
        this.loadWhatsappConversation();
      }
    }
  },
  methods: {
    ...mapActions(["listar_funcionarios", "createGlobalMessage"]),
    syncFromSale() {
      this.selectedFollowerIds = (this.sale.followers || []).map(item => String(item._id || item));
    },
    async ensureEmployees() {
      if (this.employees.length) return;
      this.loadingEmployees = true;
      try {
        await this.listar_funcionarios();
      } finally {
        this.loadingEmployees = false;
      }
    },
    async loadWhatsappConversation() {
      if (!this.client || !this.client._id) {
        this.whatsappConversation = null;
        this.messages = [];
        return;
      }
      this.loadingConversation = true;
      try {
        const response = await this.$run("whatsapp-conversations/find-by-client", {
          client_id: this.client._id
        });
        this.whatsappConversation = response.data.conversation || null;
        await this.loadMessages();
      } catch (error) {
        this.whatsappConversation = null;
        this.messages = [];
      } finally {
        this.loadingConversation = false;
      }
    },
    async loadMessages() {
      if (!this.whatsappConversation || !this.whatsappConversation._id) return;
      this.loadingMessages = true;
      try {
        const response = await this.$run("whatsapp-conversations/view", {
          conversation_id: this.whatsappConversation._id
        });
        this.whatsappConversation = response.data.conversation || this.whatsappConversation;
        this.messages = response.data.messages || [];
        this.$nextTick(this.scrollToEnd);
      } finally {
        this.loadingMessages = false;
      }
    },
    async saveFollowers() {
      if (!this.sale || !this.sale._id) return;
      this.savingFollowers = true;
      try {
        const response = await this.$run("transacoes/update-followers", {
          _id: this.sale._id,
          followers: this.selectedFollowerIds
        });
        this.$store.commit("set_gestordepedido", response.data);
        this.createGlobalMessage({ type: "success", message: "Envolvidos atualizados.", timeout: 3000 });
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: (error.response && error.response.data && error.response.data.message) || "Nao foi possivel atualizar envolvidos.",
          timeout: 5000
        });
      } finally {
        this.savingFollowers = false;
      }
    },
    async ensureClaimed() {
      if (!this.whatsappConversation || !this.whatsappConversation._id) return false;
      if (this.whatsappConversation.assigned_user) return true;
      const response = await this.$run("whatsapp-conversations/assign", {
        conversation_id: this.whatsappConversation._id
      });
      this.whatsappConversation = response.data.conversation;
      return true;
    },
    async sendMessage() {
      const text = this.newMessage.trim();
      if (!text || !this.whatsappConversation) return;
      this.sending = true;
      try {
        await this.ensureClaimed();
        const response = await this.$run("whatsapp-conversations/send-message", {
          conversation_id: this.whatsappConversation._id,
          text,
          message_type: "text"
        });
        await sendWhatsappMessage({
          chatId: this.whatsappConversation.chat_id,
          clientMessageId: response.data.message._id,
          text,
          messageType: "text"
        });
        this.messages.push(response.data.message);
        this.whatsappConversation = response.data.conversation || this.whatsappConversation;
        this.newMessage = "";
        this.$nextTick(this.scrollToEnd);
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: (error.response && error.response.data && error.response.data.message) || error.message || "Nao foi possivel enviar a mensagem.",
          timeout: 5000
        });
      } finally {
        this.sending = false;
      }
    },
    openWhatsappConversation() {
      if (!this.whatsappConversation || !this.whatsappConversation._id) return;
      this.$router.push({
        path: "/whatsapp-atendimento",
        query: { conversationId: this.whatsappConversation._id }
      });
    },
    handleIncomingMessage(event) {
      const payload = event.detail || {};
      if (!this.whatsappConversation || String(payload.conversation && payload.conversation._id) !== String(this.whatsappConversation._id)) return;
      if (payload.message && !this.messages.some(message => String(message._id) === String(payload.message._id))) {
        this.messages.push(payload.message);
        this.$nextTick(this.scrollToEnd);
      }
    },
    scrollToEnd() {
      const stream = this.$refs.messageStream;
      if (stream) stream.scrollTop = stream.scrollHeight;
    },
    messagePreview(message) {
      const labels = { image: "Imagem", audio: "Audio", video: "Video", document: "Documento", poll: "Enquete" };
      return labels[message.message_type] || "Mensagem";
    },
    formatDate(date) {
      return date ? this.$moment(date).format("HH:mm") : "";
    },
    statusIcon(status) {
      if (status === "read") return "mdi-check-all";
      if (status === "delivered") return "mdi-check-all";
      if (status === "sent") return "mdi-check";
      return "mdi-clock-outline";
    }
  },
  mounted() {
    this.ensureEmployees();
    window.addEventListener("stonepro:whatsapp-message", this.handleIncomingMessage);
  },
  beforeDestroy() {
    window.removeEventListener("stonepro:whatsapp-message", this.handleIncomingMessage);
  }
};
</script>

<style scoped>
.sale-whatsapp-panel {
  background: #f0f2f5;
  border-radius: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.sale-whatsapp-panel.dark {
  background: #0b141a;
  color: #e9edef;
}
.sale-whatsapp-topbar {
  align-items: center;
  background: #f7fbf9;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 16px;
}
.dark .sale-whatsapp-topbar {
  background: #202c33;
}
.client-identity {
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
}
.client-identity strong,
.client-identity small {
  display: block;
}
.client-identity small,
.sale-whatsapp-status {
  color: #667781;
  font-size: 12px;
}
.dark .client-identity small,
.dark .sale-whatsapp-status {
  color: #aebac1;
}
.topbar-actions,
.sale-whatsapp-status,
.sale-whatsapp-composer {
  align-items: center;
  display: flex;
  gap: 8px;
}
.sale-whatsapp-status {
  background: rgba(37, 211, 102, 0.08);
  padding: 7px 16px;
}
.sale-whatsapp-stream {
  background: #efeae2;
  background-image: radial-gradient(rgba(17, 27, 33, 0.05) 1px, transparent 1px);
  background-size: 18px 18px;
  max-height: 260px;
  min-height: 180px;
  overflow-y: auto;
  padding: 14px 18px;
}
.dark .sale-whatsapp-stream {
  background: #0b141a;
}
.stream-empty {
  align-items: center;
  color: #667781;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 150px;
  justify-content: center;
  text-align: center;
}
.message-row {
  display: flex;
  margin: 5px 0;
}
.message-row.outgoing {
  justify-content: flex-end;
}
.message-bubble {
  background: #ffffff;
  border-radius: 10px 10px 10px 2px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);
  max-width: 72%;
  padding: 7px 9px;
}
.outgoing .message-bubble {
  background: #d9fdd3;
  border-radius: 10px 10px 2px 10px;
}
.dark .message-bubble {
  background: #202c33;
}
.dark .outgoing .message-bubble {
  background: #005c4b;
}
.message-bubble small {
  align-items: center;
  color: #667781;
  display: flex;
  font-size: 10px;
  gap: 3px;
  justify-content: flex-end;
  margin-top: 2px;
}
.message-media {
  border-radius: 8px;
  max-height: 150px;
  max-width: 220px;
}
.message-audio {
  max-width: 230px;
}
.sale-whatsapp-composer {
  background: #f0f2f5;
  padding: 10px 14px;
}
.dark .sale-whatsapp-composer {
  background: #202c33;
}
.followers-menu {
  border-radius: 14px;
  padding: 14px;
  width: 360px;
}
.followers-menu.dark {
  background: #202c33;
  color: #e9edef;
}
.followers-title {
  font-weight: 700;
  margin-bottom: 10px;
}
</style>
